import { WeatherSettings, WeatherSettingsChangeSource } from '@fe/weather';
import { DropDownListChangeEvent, DropDownListProps } from '@progress/kendo-react-dropdowns';
import {
    CheckboxChangeEvent,
    CheckboxProps,
    NumericTextBoxChangeEvent,
    NumericTextBoxProps,
    RadioGroupChangeEvent,
    RadioGroupProps,
    SliderChangeEvent,
    SliderProps
} from '@progress/kendo-react-inputs';
import React, { useCallback, useEffect } from 'react';
import { useAppDispatch } from '../../../../../app/hooks/store-hooks';
import { patchFocusedWorkspaceView } from '../../../../../app/state/store';
import { weatherSettingsToUdView } from '../../../../../udView/weather-settings-from-view';
import useWeatherService from '../../../../hooks/use-weather-service';
import { DropdownItems } from '../../settings-options';

type PickKeys<T, Type> = { [K in keyof T]: T[K] extends Type ? K : never }[keyof T];

// TODO: get rid of setting stored in weather plugin and use redux

export function useWeatherSetting<T>(currentValueGetter: (settings: WeatherSettings) => T): [T, (value: T) => void] {
    const weatherService = useWeatherService();
    const dispatch = useAppDispatch();
    const [value, setValue] = React.useState<T>(() => currentValueGetter(weatherService.getSettings()));

    useEffect(() => {
        setValue(currentValueGetter(weatherService.getSettings()));
        return weatherService.on('view.changed', () => {
            setValue(currentValueGetter(weatherService.getSettings()));
        }).unsubscribe;
    }, [weatherService]);

    const _setValue = useCallback(
        (value: T) => {
            setValue(value);
            // FIXME: this is poor. On every setting change we need to update ALL settings in udview.
            // Move weather settings from map plugin into redux
            dispatch(patchFocusedWorkspaceView((x) => (x.weather.settings = weatherSettingsToUdView(weatherService.getSettings()))));
        },
        [weatherService]
    );

    return [value, _setValue];
}

export function useDropdownSetting<T, K extends keyof WeatherSettings>(
    data: DropdownItems<T>,
    key1: K,
    key2: PickKeys<WeatherSettings[K], number | null>,
    source: WeatherSettingsChangeSource
): Pick<DropDownListProps, 'data' | 'value' | 'onChange' | 'textField' | 'dataItemKey'> {
    const weatherService = useWeatherService();
    const [value, setValue] = useWeatherSetting((settings) => data.find((x) => x.value === settings[key1][key2])!);

    const onChange = React.useCallback(
        (e: DropDownListChangeEvent) => {
            weatherService.updateSettings((s) => (s[key1][key2] = e.value.value), source);
            setValue(e.value);
        },
        [weatherService]
    );

    return {
        data,
        value,
        onChange,
        dataItemKey: 'value',
        textField: 'text'
    };
}

export function useBooleanSetting<K extends keyof WeatherSettings>(
    key1: K,
    key2: PickKeys<WeatherSettings[K], boolean>,
    source: WeatherSettingsChangeSource
): Pick<CheckboxProps, 'value' | 'onChange'> {
    const weatherService = useWeatherService();
    const [value, setValue] = useWeatherSetting((settings) => settings[key1][key2] as boolean);
    const onChange = React.useCallback(
        (e: CheckboxChangeEvent) => {
            weatherService.updateSettings((s) => (s[key1][key2] = e.value as any), source);
            setValue(e.value);
        },
        [weatherService]
    );

    return {
        value,
        onChange
    };
}

export function useNumericSetting<K extends keyof WeatherSettings>(
    key1: K,
    key2: PickKeys<WeatherSettings[K], number | null>,
    source: WeatherSettingsChangeSource
): Pick<NumericTextBoxProps, 'value' | 'onChange'> {
    const weatherService = useWeatherService();
    const [value, setValue] = useWeatherSetting((settings) => settings[key1][key2] as number | null);
    const onChange = React.useCallback(
        (e: NumericTextBoxChangeEvent) => {
            weatherService.updateSettings((s) => (s[key1][key2] = e.value as any), source);
            setValue(e.value);
            // TODO: debounce
        },
        [weatherService]
    );

    return {
        value,
        onChange
    };
}

export function useRadioGroupSetting<K extends keyof WeatherSettings>(
    data: DropdownItems<any>,
    key1: K,
    key2: PickKeys<WeatherSettings[K], number | null>,
    source: WeatherSettingsChangeSource
): Pick<RadioGroupProps, 'value' | 'onChange' | 'data'> {
    const weatherService = useWeatherService();
    const [value, setValue] = useWeatherSetting((settings) => settings[key1][key2] as number | null);
    const onChange = React.useCallback(
        (e: RadioGroupChangeEvent) => {
            weatherService.updateSettings((s) => (s[key1][key2] = e.value), source);
            setValue(e.value);
        },
        [weatherService]
    );

    return {
        data: data.map((x) => ({ value: x.value, label: x.text })),
        value,
        onChange
    };
}

export function useSliderSetting<K extends keyof WeatherSettings>(
    key1: K,
    key2: PickKeys<WeatherSettings[K], number>,
    source: WeatherSettingsChangeSource
): Pick<SliderProps, 'value' | 'onChange'> {
    const weatherService = useWeatherService();
    const [value, setValue] = useWeatherSetting((settings) => settings[key1][key2] as number);

    const onChange = React.useCallback(
        (e: SliderChangeEvent) => {
            weatherService.updateSettings((s) => (s[key1][key2] = e.value as any), source);
            setValue(e.value);
        },
        [weatherService]
    );

    return {
        value,
        onChange
    };
}
